import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import * as FormActions from './form.actions';
import {
  mergeMap,
  catchError,
  map,
  of,
  from,
  EMPTY,
  concatMap,
  firstValueFrom,
} from 'rxjs';
import { QuestionnaireResponseService } from '../home/questionnaire.response.service';
import { Store } from '@ngrx/store';
import {
  selectDetail,
  selectQr,
  selectQuestionnaire,
  selectShouldSave,
} from './form.selectors';
import { Status } from '../models/Status';
import { ToastService } from '../error/toast/toast.service';
import { NoChangeError } from '../models/NoChangeError';
import { AdaptiveQuestionnaireService } from '../home/adaptive-questionnaire.service';
import { Detail } from '../models/Detail';

@Injectable()
export class QrEditEffects {
  private readonly actions = inject(Actions);
  private readonly qrService = inject(QuestionnaireResponseService);
  private readonly adaptiveQService = inject(AdaptiveQuestionnaireService);
  private readonly store = inject(Store);
  private readonly toastService = inject(ToastService);

  setInitialValues$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.setInitialValues),
      mergeMap((action) => {
        if (action.populateError) {
          return this.catchToastErrors(action.populateError);
        }
        return EMPTY;
      }),
    );
  });

  updateQr$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.updateQr),
      concatMap((action) =>
        from(this.buildUpdatedQr(action.detail)).pipe(
          map((qr) => FormActions.updateQrSuccess({ qr })),
          catchError((err) => this.catchToastErrors(err)),
        ),
      ),
    );
  });

  // Reads the latest state per edit (not a stale emit-time snapshot) so rapid edits don't clobber each other.
  private async buildUpdatedQr(detail: Detail) {
    const qr = await firstValueFrom(this.store.select(selectQr));
    const questionnaire = await firstValueFrom(
      this.store.select(selectQuestionnaire),
    );
    return this.qrService.updateQR(questionnaire, qr, detail);
  }

  updateQrSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.updateQrSuccess),
      concatLatestFrom(() => this.store.select(selectShouldSave)),
      mergeMap(([, shouldSave]) => {
        if (shouldSave) {
          return of(FormActions.saveQr());
        }
        return of(FormActions.setSaveStatus({ saveStatus: Status.UNSAVED }));
      }),
    );
  });

  saveQr$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.saveQr),
      // TODO(backend-concurrency): under fast interleaved autosave, qr and detail
      // can mismatch (e.g. qrA + a later attachment-remove detail) and out-of-order
      // PUTs can diverge from the UI. Needs versioned (If-Match) / debounced writes.
      concatLatestFrom(() => [
        this.store.select(selectQr),
        this.store.select(selectDetail),
      ]),
      concatMap(([, stateQr, detail]) =>
        from(this.qrService.saveQR(stateQr, detail)).pipe(
          map((qr) => FormActions.saveQrSuccess({ qr })),
          catchError((err) => this.catchToastErrors(err)),
        ),
      ),
    );
  });

  nextQuestion$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.nextQuestion),
      concatMap((action) =>
        from(this.adaptiveQService.nextQuestion(action.qr)).pipe(
          map(({ qr, questionnaire }) =>
            FormActions.nextQuestionSuccess({ qr, questionnaire }),
          ),
          catchError((err) => this.catchToastErrors(err)),
        ),
      ),
    );
  });

  nextQuestionSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(FormActions.nextQuestionSuccess),
      concatLatestFrom(() => this.store.select(selectShouldSave)),
      mergeMap(([, shouldSave]) => {
        if (shouldSave) {
          return of(FormActions.saveQr());
        }
        return of(FormActions.setSaveStatus({ saveStatus: Status.UNSAVED }));
      }),
    );
  });

  catchToastErrors(err) {
    if (err instanceof NoChangeError) {
      console.log(err.message);
    } else this.toastService.showErrorToast(err);
    return of(FormActions.setSaveStatus({ saveStatus: Status.UNSAVED }));
  }
}
